import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, AlertCircle, Copy, Check, Mail, User, MapPin, Zap, FileText, AlertTriangle, Search, PencilLine, Plus, Trash2, PhoneCall } from 'lucide-react';
import { UnidadeConsumidora } from '../../types';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';
import { podeEditarCadastro } from '../../src/lib/permissaoCadastro';
import UCSearchPicker, { composeEnderecoUC } from './UCSearchPicker';
import SignatariosSelector, { signatariosVazio } from '../../src/components/SignatariosSelector';
import { SignatariosEscolhidos } from '../../src/lib/termoSignatarios';
import { RegeracaoDocumento, doPayload, arrayDoPayload } from './regeracaoDocumento';
import { vincularTermoGerado } from '../../src/lib/cancelamentos';
import { sugerirSaldoDistrato, fmtNumeroDistrato } from '../../src/lib/saldoDistrato';
import type { SugestaoSaldoDistrato, FaturaParaSaldoDistrato } from '../../src/lib/saldoDistrato';
import { ucCodigoKeys } from '../../src/lib/ucCodigo';
import ConfirmModal from '../../src/components/ConfirmModal';
import { useFechamentoSeguro } from '../../src/hooks/useFechamentoSeguro';
import { useCompletarDadosUc } from '../../src/hooks/useCompletarDadosUc';
import { buscarFaturasEmAberto, carregarDadosDocumentoUc } from '../../src/services/dadosDocumentoUc';
import { montarDadosDocumentoUc, seVazio, somaVencidas, type DadosDocumentoUc } from '../../src/lib/dadosDocumentoUc';
import { avisoPrevioDias as lerAvisoPrevioDias, calcularDataEfetiva, problemaDataEfetiva, AVISO_PREVIO_DIAS_PADRAO } from '../../src/lib/dataEfetivaDistrato';
import { ehEmailDaVigor, motivoDevolucaoLegivel, normalizarEmail } from '../../supabase/functions/_shared/emailDevolvido.ts';

interface CancelUCModalProps {
    isOpen: boolean;
    onClose: () => void;
    unidade?: UnidadeConsumidora | null;
    /** Preenche o formulário com a emissão anterior, quando veio de "Corrigir e gerar novamente". */
    regeracao?: RegeracaoDocumento | null;
    /** Quando o distrato nasce a partir da aba Cancelamentos, carimba o elo em termo_cancelamento_logs. */
    solicitacaoId?: number | null;
}

interface UCEntry {
    ucId?: number;
    numero: string;
    endereco: string;
    situacao: string;
}

interface ContatoEntry {
    data: string;
    meio: string;
    resultado: string;
}

const MAX_UCS = 3;
const MAX_CONTATOS = 3;

const inputCls = "w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100";
const labelCls = "block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider";
const sectionTitleCls = "text-sm font-bold text-gray-800 dark:text-slate-200 border-b border-gray-100 dark:border-slate-700 pb-2 flex items-center gap-2";

const CancelUCModal: React.FC<CancelUCModalProps> = ({ isOpen, onClose, unidade, regeracao, solicitacaoId }) => {
    const { addToast } = useToast();
    const { user, profile } = useAuth();
    const [loading, setLoading] = useState(false);
    const [successData, setSuccessData] = useState<{ link: string; numero: string } | null>(null);
    const [copied, setCopied] = useState(false);
    // Vira true no primeiro toque do operador. O preenchimento automatico pela UC
    // nao conta: nao ha trabalho a perder ainda.
    const [rascunho, setRascunho] = useState(false);

    // Seletor de modelo
    const [modeloDistrato, setModeloDistrato] = useState<'padrao' | 'inadimplencia'>('padrao');

    // Modo de preenchimento
    const [modo, setModo] = useState<'buscar' | 'manual'>('buscar');
    const [ucsSelecionadas, setUcsSelecionadas] = useState<UnidadeConsumidora[]>([]);

    // Cliente
    const [nome, setNome] = useState('');
    const [cpfCnpj, setCpfCnpj] = useState('');
    const [tipoCliente, setTipoCliente] = useState<'PF' | 'PJ'>('PF');

    const handleCpfCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/\D/g, '');
        if (tipoCliente === 'PF') {
            if (val.length > 11) val = val.slice(0, 11);
            val = val.replace(/(\d{3})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        } else {
            if (val.length > 14) val = val.slice(0, 14);
            val = val.replace(/^(\d{2})(\d)/, '$1.$2');
            val = val.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
            val = val.replace(/\.(\d{3})(\d)/, '.$1/$2');
            val = val.replace(/(\d{4})(\d)/, '$1-$2');
        }
        setCpfCnpj(val);
    };

    // Contato
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    // Avisos da conferência antes de emitir. Não-nulo = confirmação aberta.
    const [conferencia, setConferencia] = useState<{ avisos: string[]; emailJaDevolvido: boolean } | null>(null);

    // Endereço do associado
    const [cep, setCep] = useState('');
    const [rua, setRua] = useState('');
    const [numero, setNumero] = useState('');
    const [bairro, setBairro] = useState('');
    const [cidade, setCidade] = useState('');
    const [estado, setEstado] = useState('');

    // UCs envolvidas (até 3)
    const emptyUcEntry = (): UCEntry => ({ numero: '', endereco: '', situacao: 'Ativa' });
    const [ucs, setUcs] = useState<UCEntry[]>([emptyUcEntry()]);

    const [concessionaria, setConcessionaria] = useState('Equatorial Goiás');

    // Representante legal da Associação + testemunhas (default: Config → Assinaturas do Termo)
    const [signatarios, setSignatarios] = useState<SignatariosEscolhidos>(signatariosVazio);

    // Condições financeiras (Padrão - seção 6)
    const [saldoKwh, setSaldoKwh] = useState('');
    const [valorSaldo, setValorSaldo] = useState('');
    const [opcoesMulta, setOpcoesMulta] = useState<string[]>([]);
    const [multaRescisoria, setMultaRescisoria] = useState('');
    const [totalDebito, setTotalDebito] = useState('');
    const [mesesEmAtraso, setMesesEmAtraso] = useState('');
    const [outrosDebitos, setOutrosDebitos] = useState('');
    const [formaPagamento, setFormaPagamento] = useState<'a_vista' | 'parcelado' | 'compensacao' | ''>('');
    const [dataPagamentoAVista, setDataPagamentoAVista] = useState('');
    const [numParcelas, setNumParcelas] = useState('');
    const [valorParcela, setValorParcela] = useState('');
    const [dataPrimeiraParcela, setDataPrimeiraParcela] = useState('');

    // Inadimplência
    const [motivosInadi, setMotivosInadi] = useState<string[]>([]);
    const [faturasEmAtraso, setFaturasEmAtraso] = useState('');
    const [multaFidelidade, setMultaFidelidade] = useState('');
    const emptyContato = (): ContatoEntry => ({ data: '', meio: '', resultado: '' });
    const [contatos, setContatos] = useState<ContatoEntry[]>([emptyContato()]);

    const toggleOpcaoMulta = (opcao: string) => {
        setOpcoesMulta(prev => prev.includes(opcao) ? prev.filter(o => o !== opcao) : [...prev, opcao]);
    };
    const toggleMotivoInadi = (motivo: string) => {
        setMotivosInadi(prev => prev.includes(motivo) ? prev.filter(m => m !== motivo) : [...prev, motivo]);
    };

    // Motivo do Desligamento (apenas Padrão) + Observações
    const [motivoDesligamento, setMotivoDesligamento] = useState('');
    const [obs, setObs] = useState('');
    const [dataSolicitacao, setDataSolicitacao] = useState(new Date().toISOString().split('T')[0]);
    // Data efetiva do desligamento (#6135): solicitação + aviso prévio do contrato.
    // Acompanha a data da solicitação até alguém editar à mão.
    const [prazoAvisoDias, setPrazoAvisoDias] = useState(AVISO_PREVIO_DIAS_PADRAO);
    const [dataEfetiva, setDataEfetiva] = useState('');
    const [dataEfetivaManual, setDataEfetivaManual] = useState(false);
    useEffect(() => {
        if (!isOpen) return;
        let vivo = true;
        supabase.from('system_settings').select('value').eq('key', 'cancelamento_aviso_previo_dias').maybeSingle()
            .then(({ data, error }) => {
                if (error) console.error('[CancelUCModal] aviso prévio', error);
                if (vivo) setPrazoAvisoDias(lerAvisoPrevioDias(data?.value));
            });
        return () => { vivo = false; };
    }, [isOpen]);
    useEffect(() => {
        if (!dataEfetivaManual) setDataEfetiva(calcularDataEfetiva(dataSolicitacao, prazoAvisoDias));
    }, [dataSolicitacao, prazoAvisoDias, dataEfetivaManual]);

    const preencherDoCliente = (uc: UnidadeConsumidora) => {
        // Nome e CPF/CNPJ têm que sair da MESMA pessoa. Quem assina o distrato é o
        // cliente contratante da UC (bloco "DADOS DO CLIENTE"); o titular da fatura da
        // distribuidora é só fallback, quando o cliente da UC está vazio. Invertido,
        // o termo saía com o nome do cliente e o CPF do titular.
        // A regra mora em montarDadosDocumentoUc, a mesma dos outros três documentos.
        const d = montarDadosDocumentoUc(uc);
        setNome(d.nome);
        setCpfCnpj(d.cpfCnpj);
        setTipoCliente(d.tipoCliente);
        setEmail(d.email);
        setWhatsapp(d.whatsapp);
        setCep(d.endereco.cep);
        setRua(d.endereco.logradouro);
        setNumero(d.endereco.numero);
        setBairro(d.endereco.bairro);
        setCidade(d.endereco.cidade);
        setEstado(d.endereco.estado);
        if (uc.concessionaria) setConcessionaria(uc.concessionaria);
    };

    // O que o cadastro da UC não tem vem da negociação e da última fatura,
    // e só entra em campo vazio (ou no que o preenchimento automático pôs e
    // ninguém mexeu — o endereço com rua e sem bairro, por exemplo).
    const { completar, esquecer } = useCompletarDadosUc();
    const aplicarComplemento = (uc: UnidadeConsumidora, d: DadosDocumentoUc) => {
        const auto = montarDadosDocumentoUc(uc);
        setNome(seVazio(d.nome));
        if (!auto.cpfCnpj && d.cpfCnpj) {
            setCpfCnpj(seVazio(d.cpfCnpj));
            setTipoCliente(d.tipoCliente);
        }
        setEmail(seVazio(d.email));
        setWhatsapp(seVazio(d.whatsapp));
        setCep(seVazio(d.endereco.cep, auto.endereco.cep));
        setRua(seVazio(d.endereco.logradouro, auto.endereco.logradouro));
        setNumero(seVazio(d.endereco.numero, auto.endereco.numero));
        setBairro(seVazio(d.endereco.bairro, auto.endereco.bairro));
        setCidade(seVazio(d.endereco.cidade, auto.endereco.cidade));
        setEstado(seVazio(d.endereco.estado, auto.endereco.estado));
        completarEnderecoDaUc(uc, d);
    };
    /** Endereço da linha da UC na lista de UCs do distrato. */
    const completarEnderecoDaUc = (uc: UnidadeConsumidora, d: DadosDocumentoUc) => {
        const preencher = seVazio(d.enderecoTexto, composeEnderecoUC(uc));
        setUcs(prev => prev.map(e => e.ucId === uc.id ? { ...e, endereco: preencher(e.endereco) } : e));
    };

    useEffect(() => {
        if (isOpen) {
            setModeloDistrato('padrao');
            setSuccessData(null);
            setRascunho(false);
            setMotivoDesligamento('');
            setObs('');
            setDataSolicitacao(new Date().toISOString().split('T')[0]);
            setDataEfetivaManual(false);
            setSaldoKwh('');
            setValorSaldo('');
            setOpcoesMulta([]);
            setMultaRescisoria('');
            setTotalDebito('');
            setMesesEmAtraso('');
            setOutrosDebitos('');
            setFormaPagamento('');
            setDataPagamentoAVista('');
            setNumParcelas('');
            setValorParcela('');
            setDataPrimeiraParcela('');
            setMotivosInadi([]);
            setFaturasEmAtraso('');
            setMultaFidelidade('');
            setContatos([emptyContato()]);
            setSignatarios(signatariosVazio);

            if (unidade) {
                setModo('buscar');
                setUcsSelecionadas([unidade]);
                preencherDoCliente(unidade);
                setUcs([{
                    ucId: unidade.id,
                    numero: unidade.numero_uc || '',
                    endereco: composeEnderecoUC(unidade),
                    situacao: unidade.situacao || 'Ativa'
                }]);
                completar(unidade, d => aplicarComplemento(unidade, d));
            } else {
                esquecer();
                setModo('buscar');
                setUcsSelecionadas([]);
                setNome('');
                setCpfCnpj('');
                setTipoCliente('PF');
                setEmail('');
                setWhatsapp('');
                setCep('');
                setRua('');
                setNumero('');
                setBairro('');
                setCidade('');
                setEstado('');
                setConcessionaria('Equatorial Goiás');
                setUcs([emptyUcEntry()]);
            }

            // Regeração ("Corrigir e gerar novamente"): o distrato cancelado volta inteiro,
            // porque é exatamente o que o operador quer revisar — as condições financeiras
            // e o histórico de contatos não têm como ser deduzidos da UC.
            // `data_solicitacao` NÃO volta: o documento novo é de hoje.
            const p = regeracao?.payload;
            if (p) {
                if (p.modelo_distrato === 'padrao' || p.modelo_distrato === 'inadimplencia') setModeloDistrato(p.modelo_distrato);
                const docAnterior = doPayload(p, 'cpf_cnpj');
                if (doPayload(p, 'cliente_nome')) setNome(doPayload(p, 'cliente_nome'));
                if (docAnterior) {
                    setCpfCnpj(docAnterior);
                    setTipoCliente(docAnterior.replace(/\D/g, '').length === 14 ? 'PJ' : 'PF');
                }
                if (p.tipo_cliente === 'PF' || p.tipo_cliente === 'PJ') setTipoCliente(p.tipo_cliente);
                if (doPayload(p, 'email')) setEmail(doPayload(p, 'email'));
                if (doPayload(p, 'whatsapp')) setWhatsapp(doPayload(p, 'whatsapp'));
                if (doPayload(p, 'cep')) setCep(doPayload(p, 'cep'));
                if (doPayload(p, 'rua')) setRua(doPayload(p, 'rua'));
                if (doPayload(p, 'numero')) setNumero(doPayload(p, 'numero'));
                if (doPayload(p, 'bairro')) setBairro(doPayload(p, 'bairro'));
                if (doPayload(p, 'cidade')) setCidade(doPayload(p, 'cidade'));
                if (doPayload(p, 'estado')) setEstado(doPayload(p, 'estado'));
                if (doPayload(p, 'nome_concessionaria')) setConcessionaria(doPayload(p, 'nome_concessionaria'));

                const ucsAnteriores = arrayDoPayload<{ numero?: string; endereco?: string; situacao?: string }>(p, 'ucs');
                if (ucsAnteriores.length > 0) {
                    // O payload não guarda o ucId; recupera das UCs já selecionadas pelo número.
                    const idPorNumero = new Map(ucsSelecionadas.concat(unidade ? [unidade] : [])
                        .filter(u => u?.numero_uc)
                        .map(u => [String(u.numero_uc), u.id]));
                    setUcs(ucsAnteriores.slice(0, MAX_UCS).map(u => ({
                        ucId: idPorNumero.get(String(u.numero ?? '')),
                        numero: u.numero ?? '',
                        endereco: u.endereco ?? '',
                        situacao: u.situacao ?? 'Ativa',
                    })));
                }

                setMotivoDesligamento(doPayload(p, 'motivo_desligamento'));
                setObs(doPayload(p, 'obs'));
                // Reemissão mantém as datas do documento anterior (antes voltava para hoje).
                if (/^\d{4}-\d{2}-\d{2}$/.test(doPayload(p, 'data_solicitacao'))) setDataSolicitacao(doPayload(p, 'data_solicitacao'));
                if (/^\d{4}-\d{2}-\d{2}$/.test(doPayload(p, 'data_efetiva'))) {
                    setDataEfetiva(doPayload(p, 'data_efetiva'));
                    setDataEfetivaManual(true);
                }

                // Padrão — seção 6
                setSaldoKwh(doPayload(p, 'saldo_kwh'));
                setValorSaldo(doPayload(p, 'valor_saldo'));
                setOpcoesMulta(arrayDoPayload<string>(p, 'opcoes_multa'));
                setMultaRescisoria(doPayload(p, 'multa_rescisoria'));
                setTotalDebito(doPayload(p, 'total_debito'));
                setMesesEmAtraso(doPayload(p, 'meses_em_atraso'));
                setOutrosDebitos(doPayload(p, 'outros_debitos'));
                if (['a_vista', 'parcelado', 'compensacao'].includes(p.forma_pagamento)) setFormaPagamento(p.forma_pagamento);
                setDataPagamentoAVista(doPayload(p, 'data_pagamento_a_vista'));
                setNumParcelas(doPayload(p, 'num_parcelas'));
                setValorParcela(doPayload(p, 'valor_parcela'));
                setDataPrimeiraParcela(doPayload(p, 'data_primeira_parcela'));

                // Inadimplência
                setMotivosInadi(arrayDoPayload<string>(p, 'motivos_inadi'));
                setFaturasEmAtraso(doPayload(p, 'faturas_em_atraso'));
                setMultaFidelidade(doPayload(p, 'multa_fidelidade'));
                const contatosAnteriores = arrayDoPayload<ContatoEntry>(p, 'contatos');
                setContatos(contatosAnteriores.length > 0
                    ? contatosAnteriores.slice(0, MAX_CONTATOS).map(c => ({
                        data: c.data ?? '', meio: c.meio ?? '', resultado: c.resultado ?? '',
                    }))
                    : [emptyContato()]);
            }
        }
    // `ucsSelecionadas` fica fora das dependências de propósito: ele é lido só para
    // recuperar o ucId das UCs do payload, e incluí-lo faria o efeito rodar de novo a
    // cada seleção, apagando o que o usuário já tivesse digitado.
    }, [isOpen, unidade, regeracao]);

    const handleClose = () => {
        setSuccessData(null);
        onClose();
    };

    // Depois de gerado nao ha mais o que perder — o sucesso fecha sem perguntar.
    const { propsDoFundo, pedirParaFechar, confirmandoDescarte, descartarEFechar, continuarPreenchendo } =
        useFechamentoSeguro({ isOpen, temRascunho: rascunho && !successData, onFechar: handleClose });

    // Sugestão do saldo da seção 6.1, a partir da conta (ver src/lib/saldoDistrato.ts).
    // Antes o campo abria vazio e quem gerava copiava o número errado da tela.
    const [sugestaoSaldo, setSugestaoSaldo] = useState<SugestaoSaldoDistrato | null>(null);
    const saldoAutoAplicado = useRef(false);
    const ucsDoDistrato = ucs
        .filter(u => u.numero)
        .map(u => {
            const cadastro = ucsSelecionadas.find(x => x.id === u.ucId) || (unidade && unidade.id === u.ucId ? unidade : null);
            return { chaves: ucCodigoKeys(u.numero, cadastro?.outro_numero_uc), desconto: cadastro?.desconto_tarifa ?? null };
        })
        .filter(u => u.chaves.length > 0);
    const chavesAssinatura = JSON.stringify(ucsDoDistrato);
    useEffect(() => {
        if (!isOpen) { setSugestaoSaldo(null); saldoAutoAplicado.current = false; return; }
        const lista = JSON.parse(chavesAssinatura) as { chaves: string[]; desconto: number | null }[];
        const chaves = lista.map(u => u.chaves);
        if (chaves.length === 0) { setSugestaoSaldo(null); return; }
        let vivo = true;
        supabase.from('faturas')
            .select('uc_codigo_norm, mes_referencia, created_at, status_fatura, saldo_ugs_vigor, tarifa_injecao_gd1, valor_tarifa_distribuidora, desconto_tarifa')
            .in('uc_codigo_norm', Array.from(new Set(chaves.flat())))
            .order('created_at', { ascending: false })
            .limit(300)
            .then(({ data, error }) => {
                if (!vivo) return;
                if (error) { console.error('Sugestão de saldo do distrato', error); setSugestaoSaldo(null); return; }
                setSugestaoSaldo(sugerirSaldoDistrato((data || []) as FaturaParaSaldoDistrato[], chaves, lista.map(u => u.desconto)));
            });
        return () => { vivo = false; };
    }, [isOpen, chavesAssinatura]);
    // Distrato NOVO com os campos vazios: preenche uma vez. Na correção ("Corrigir e
    // gerar novamente") o que foi digitado antes manda — a sugestão só aparece ao lado.
    useEffect(() => {
        if (!isOpen || regeracao || saldoAutoAplicado.current || !sugestaoSaldo || sugestaoSaldo.kwh === null) return;
        saldoAutoAplicado.current = true;
        if (!saldoKwh.trim()) setSaldoKwh(fmtNumeroDistrato(sugestaoSaldo.kwh));
        if (!valorSaldo.trim() && sugestaoSaldo.valor !== null) setValorSaldo(fmtNumeroDistrato(sugestaoSaldo.valor));
    }, [isOpen, regeracao, sugestaoSaldo]);

    // Faturas vencidas das UCs do distrato (6.2 e inadimplência), pela mesma regra
    // da notificação extrajudicial. Só entra em campo vazio ou no valor que este
    // efeito mesmo pôs — assim a UC adicionada depois soma, e o digitado fica.
    // Na correção ("Corrigir e gerar novamente") vale o que foi emitido antes.
    const debitoAuto = useRef('');
    useEffect(() => {
        if (!isOpen) { debitoAuto.current = ''; return; }
        if (regeracao) return;
        const chaves = Array.from(new Set((JSON.parse(chavesAssinatura) as { chaves: string[] }[]).flatMap(u => u.chaves)));
        if (chaves.length === 0) return;
        let vivo = true;
        buscarFaturasEmAberto(chaves)
            .then(faturas => {
                if (!vivo) return;
                const soma = somaVencidas(faturas);
                const txt = soma > 0 ? fmtNumeroDistrato(soma) : '';
                setFaturasEmAtraso(seVazio(txt, debitoAuto.current));
                setTotalDebito(seVazio(txt, debitoAuto.current));
                if (txt) debitoAuto.current = txt;
            })
            .catch(err => console.error('Débito vencido do distrato', err));
        return () => { vivo = false; };
    }, [isOpen, regeracao, chavesAssinatura]);

    if (!isOpen) return null;

    const handleAddUcSelecionada = (uc: UnidadeConsumidora) => {
        setRascunho(true);
        setUcsSelecionadas(prev => [...prev, uc]);
        const entry: UCEntry = {
            ucId: uc.id,
            numero: uc.numero_uc || '',
            endereco: composeEnderecoUC(uc),
            situacao: uc.situacao || 'Ativa'
        };
        setUcs(prev => {
            const preenchidas = prev.filter(e => e.numero || e.endereco);
            return [...preenchidas, entry].slice(0, MAX_UCS);
        });
        if (ucsSelecionadas.length === 0) {
            preencherDoCliente(uc);
            completar(uc, d => aplicarComplemento(uc, d));
        } else {
            // UC a mais do mesmo cliente: só o endereço da linha dela.
            carregarDadosDocumentoUc(uc).then(d => completarEnderecoDaUc(uc, d));
        }
    };

    const handleRemoveUcSelecionada = (uc: UnidadeConsumidora) => {
        setRascunho(true);
        setUcsSelecionadas(prev => prev.filter(s => s.id !== uc.id));
        setUcs(prev => {
            const restantes = prev.filter(e => e.ucId !== uc.id);
            return restantes.length > 0 ? restantes : [emptyUcEntry()];
        });
    };

    const updateUcEntry = (index: number, field: keyof UCEntry, value: string) => {
        setRascunho(true);
        setUcs(prev => prev.map((e, i) => i === index ? { ...e, [field]: value } : e));
    };

    const updateContato = (index: number, field: keyof ContatoEntry, value: string) => {
        setContatos(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
    };

    const handleGenerateCancellation = async (e: React.FormEvent) => {
        e.preventDefault();

        const ucsValidas = ucs.filter(u => u.numero.trim());

        if (!nome || !cpfCnpj || !email || !whatsapp) {
            addToast('Preencha os campos obrigatórios (Nome, CPF/CNPJ, E-mail e WhatsApp).', 'warning');
            return;
        }
        if (ucsValidas.length === 0) {
            addToast('Informe ao menos uma Unidade Consumidora.', 'warning');
            return;
        }
        if (modeloDistrato === 'padrao' && opcoesMulta.includes('A') && !multaRescisoria) {
            addToast('Opção A selecionada: informe o valor da Multa Rescisória.', 'warning');
            return;
        }
        if (modeloDistrato === 'padrao' && formaPagamento === 'parcelado' && (!numParcelas || !valorParcela || !dataPrimeiraParcela)) {
            addToast('Pagamento parcelado: informe nº de parcelas, valor da parcela e data da primeira parcela.', 'warning');
            return;
        }
        if (modeloDistrato === 'padrao' && formaPagamento === 'a_vista' && !dataPagamentoAVista) {
            addToast('Pagamento à vista: informe a data do pagamento.', 'warning');
            return;
        }
        if (modeloDistrato === 'inadimplencia' && motivosInadi.length === 0) {
            addToast('Selecione ao menos um motivo da inadimplência.', 'warning');
            return;
        }
        if (!signatarios.procurador || !signatarios.testemunha1 || !signatarios.testemunha2) {
            addToast('Selecione o representante legal e as duas testemunhas do distrato.', 'warning');
            return;
        }
        if (ehEmailDaVigor(email)) {
            addToast('Use o e-mail do cliente. Com um e-mail da Vigor, o convite de assinatura do cliente cai numa caixa nossa.', 'warning');
            return;
        }

        const problemaData = problemaDataEfetiva(dataSolicitacao, dataEfetiva);
        if (problemaData) {
            addToast(problemaData, 'warning');
            return;
        }

        setLoading(true);
        const resultado = await conferirAntesDeEmitir(normalizarEmail(email), ucIdsDe(ucsValidas));
        setLoading(false);
        if (resultado.avisos.length > 0) {
            setConferencia(resultado);
            return;
        }
        await emitirDistrato(resultado.emailJaDevolvido);
    };

    const ucIdsDe = (lista: UCEntry[]) => lista.map(u => u.ucId).filter((id): id is number => typeof id === 'number');

    /**
     * Confere, antes de gastar um documento no Autentique, os dois problemas
     * que o levantamento de 17/09/2026 achou: e-mail que já voltou antes
     * (18 reenvios para endereço morto) e distrato ainda aberto para o mesmo
     * cliente (3 reemitidos duas semanas depois sem cancelar o primeiro).
     *
     * Avisa, não trava: o link também vai por WhatsApp, então um e-mail ruim
     * não impede o cliente de assinar. Falha na consulta também não trava.
     */
    const conferirAntesDeEmitir = async (emailCliente: string, ucIds: number[]) => {
        const avisos: string[] = [];
        let emailJaDevolvido = false;
        const quando = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');
        try {
            const abertos = () => supabase
                .from('termo_cancelamento_logs')
                .select('id, created_at, uc_numero')
                .is('cancelado_em', null)
                .is('assinado_em', null);
            const [devolvido, porUc, porEmail] = await Promise.all([
                supabase
                    .from('termo_cancelamento_logs')
                    .select('email_recusado_em, email_recusa_motivo')
                    .eq('email_cliente', emailCliente)
                    .not('email_recusado_em', 'is', null)
                    .order('email_recusado_em', { ascending: false })
                    .limit(1),
                ucIds.length ? abertos().in('uc_id', ucIds) : Promise.resolve({ data: [] as Array<{ id: number; created_at: string; uc_numero: string | null }> }),
                abertos().eq('email_cliente', emailCliente),
            ]);

            const ultimaDevolucao = devolvido.data?.[0];
            if (ultimaDevolucao?.email_recusado_em) {
                emailJaDevolvido = true;
                avisos.push(
                    `O e-mail ${emailCliente} já voltou em ${quando(ultimaDevolucao.email_recusado_em)}: `
                    + `${motivoDevolucaoLegivel(ultimaDevolucao.email_recusa_motivo)}. `
                    + 'Se tiver outro e-mail do cliente, troque. Se não tiver, pode seguir — o link também vai pelo WhatsApp.'
                );
            }

            // O distrato sendo corrigido já foi cancelado antes de abrir aqui; o
            // filtro é só por garantia.
            const vistos = new Set<number>();
            const emAberto = [...(porUc.data || []), ...(porEmail.data || [])].filter(l => {
                if (l.id === regeracao?.substituiLogId || vistos.has(l.id)) return false;
                vistos.add(l.id);
                return true;
            });
            if (emAberto.length > 0) {
                const maisRecente = emAberto.sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
                avisos.push(
                    `Já existe um distrato em aberto para esse cliente, emitido em ${quando(maisRecente.created_at)}`
                    + (maisRecente.uc_numero ? ` (UC ${maisRecente.uc_numero})` : '') + '. '
                    + 'Se ele venceu ou saiu errado, use "Corrigir" no histórico de documentos — assim o antigo é cancelado e não fica valendo junto.'
                );
            }
        } catch (err) {
            console.error('Falha ao conferir distrato antes de emitir:', err);
        }
        return { avisos, emailJaDevolvido };
    };

    const emitirDistrato = async (emailJaDevolvido: boolean) => {
        const ucsValidas = ucs.filter(u => u.numero.trim());
        const emailCliente = normalizarEmail(email);

        setLoading(true);

        try {
            const { data: settings } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'cancellation_term_webhook_url')
                .single();

            const webhookUrl = settings?.value || '';

            if (!webhookUrl) {
                addToast('URL do Webhook de Emissão de Termo de Cancelamento não configurada em Config > APIs.', 'error');
                setLoading(false);
                return;
            }

            const payload = {
                id: ucsValidas[0]?.ucId || null,
                uc_ids: ucsValidas.map(u => u.ucId).filter(Boolean),
                cliente_nome: nome,
                cpf_cnpj: cpfCnpj,
                tipo_cliente: tipoCliente,
                email: emailCliente,
                whatsapp: whatsapp,
                rua: rua,
                numero: numero,
                bairro: bairro,
                cidade: cidade,
                estado: estado,
                cep: cep,
                ucs: ucsValidas.map(u => ({
                    numero: u.numero,
                    endereco: u.endereco,
                    situacao: u.situacao
                })),
                nome_concessionaria: concessionaria,
                motivo_desligamento: motivoDesligamento,
                obs: obs,
                data_solicitacao: dataSolicitacao,
                // O n8n usa esta data no lugar dos 30 dias fixos (#6135).
                data_efetiva: dataEfetiva,
                modelo_distrato: modeloDistrato,
                // Chaves legadas (placeholders {{Representante Legal Associacao}} / {{CPF Representante Associacao}})
                representante_legal: signatarios.procurador?.nome || '',
                cpf_representante: signatarios.procurador?.cpf || '',
                // Signatários internos: rep. legal assina e testemunhas testemunham junto com o associado
                procurador: signatarios.procurador,
                testemunha1: signatarios.testemunha1,
                testemunha2: signatarios.testemunha2,
                // Padrão — seção 6
                saldo_kwh: saldoKwh,
                valor_saldo: valorSaldo,
                opcoes_multa: opcoesMulta,
                multa_rescisoria: multaRescisoria,
                total_debito: totalDebito,
                meses_em_atraso: mesesEmAtraso,
                outros_debitos: outrosDebitos,
                forma_pagamento: formaPagamento,
                data_pagamento_a_vista: dataPagamentoAVista,
                num_parcelas: numParcelas,
                valor_parcela: valorParcela,
                data_primeira_parcela: dataPrimeiraParcela,
                // Inadimplência
                motivos_inadi: motivosInadi,
                faturas_em_atraso: faturasEmAtraso,
                multa_fidelidade: multaFidelidade,
                contatos: contatos.filter(c => c.data || c.meio || c.resultado),
                // Chave legada (compatibilidade com versão anterior do fluxo n8n)
                uc01: ucsValidas[0]?.numero || ''
            };

            const response = await fetch(webhookUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const responseText = await response.text();
                let n8nResponse: any = null;
                if (responseText) {
                    try {
                        n8nResponse = JSON.parse(responseText);
                    } catch {
                        console.warn('Resposta do webhook não é JSON:', responseText);
                    }
                }

                if (n8nResponse && n8nResponse.link_assinatura_cliente) {
                    setSuccessData({
                        link: n8nResponse.link_assinatura_cliente,
                        numero: n8nResponse.numero_termo || ''
                    });
                    logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'cancelamentos', acao: 'gerar_termo_cancelamento', origem: 'acao' });

                    let logCriadoId: number | null = null;
                    try {
                        const { data: logCriado } = await supabase.from('termo_cancelamento_logs').insert({
                            uc_id: ucsValidas[0]?.ucId || null,
                            uc_numero: ucsValidas.map(u => u.numero).join(', '),
                            cliente_nome: nome,
                            // PDF público do Autentique — passa a existir quando todos assinam (o link de assinatura expira)
                            url_pdf: n8nResponse.id_autentique
                                ? `https://api.autentique.com.br/documentos/${n8nResponse.id_autentique}/assinado.pdf`
                                : (n8nResponse.url_pdf || n8nResponse.link_assinatura_cliente),
                            id_autentique: n8nResponse.id_autentique || null,
                            numero_termo: n8nResponse.numero_termo || null,
                            user_id: user?.id || null,
                            tipo_cliente: tipoCliente,
                            modelo_distrato: modeloDistrato,
                            motivo_desligamento: motivoDesligamento || null,
                            observacoes: obs || null,
                            // Snapshot do formulário: o Autentique não deixa editar um documento
                            // criado, então "corrigir" é cancelar + emitir de novo — e é daqui
                            // que o modal renasce preenchido.
                            payload_geracao: payload,
                            // Consultado na próxima emissão: "esse e-mail já voltou?"
                            // A reconciliação preenche email_recusado_em.
                            email_cliente: emailCliente,
                            // Quando esta emissão é a correção de uma anterior, guarda o elo.
                            substitui_log_id: regeracao?.substituiLogId ?? null
                        }).select('id').single();
                        logCriadoId = logCriado?.id ?? null;
                    } catch (logError) {
                        console.error('Error logging cancellation term:', logError);
                    }

                    // O e-mail digitado aqui só existia dentro do documento: o cadastro
                    // da UC ficava vazio (87 de 91 distratos em 17/09/2026) e cada
                    // emissão recomeçava do zero. Grava na UC — menos endereço que já
                    // voltou, para não espalhar e-mail morto pelos outros documentos.
                    // #6239: e-mail é dado cadastral — sem a permissão, fica só no documento.
                    const ucIds = ucIdsDe(ucsValidas);
                    if (ucIds.length > 0 && !emailJaDevolvido && podeEditarCadastro(profile)) {
                        const { error: erroEmailUc } = await supabase
                            .from('unidades_consumidoras')
                            .update({ email_cliente: emailCliente })
                            .in('id', ucIds);
                        if (erroEmailUc) console.error('Distrato gerado, mas o e-mail não foi salvo na UC:', erroEmailUc);
                    }

                    // Elo com a aba Cancelamentos: se isto falhar silenciosamente, a solicitação
                    // continua marcada como "sem distrato" mesmo com o documento já enviado para
                    // assinatura — e alguém pode gerar um segundo distrato para o mesmo cliente.
                    // Por isso tem tratamento de erro próprio, avisando quem gerou.
                    if (solicitacaoId && logCriadoId) {
                        try {
                            await vincularTermoGerado(solicitacaoId, logCriadoId);
                        } catch (vinculoError) {
                            console.error('Erro ao vincular distrato à solicitação de cancelamento:', vinculoError);
                            addToast('Distrato gerado, mas não foi possível vinculá-lo à solicitação em Cancelamentos. Avise o financeiro para conferir manualmente e evitar gerar um segundo distrato.', 'warning');
                        }
                    }

                    addToast('Distrato gerado com sucesso!', 'success');
                } else {
                    throw new Error('Retorno inválido do webhook — Link não encontrado na resposta.');
                }
            } else {
                throw new Error(`Erro ${response.status}: ${response.statusText}`);
            }

        } catch (error: any) {
            console.error('Erro ao gerar termo de cancelamento:', error);
            addToast('Erro ao gerar distrato: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = () => {
        if (successData?.link) {
            navigator.clipboard.writeText(successData.link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return createPortal(
        <>
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsDoFundo}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-500 rounded-lg border border-red-100 dark:border-red-800/50">
                            <AlertCircle size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">Gerar Distrato</h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400">Selecione o modelo e confirme os dados do cliente.</p>
                        </div>
                    </div>
                    <button
                        onClick={pedirParaFechar}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {successData ? (
                        <div className="flex flex-col items-center justify-center text-center space-y-4 py-6">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mb-2">
                                <Check size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">Distrato Gerado com Sucesso!</h3>
                            <p className="text-sm text-gray-500 dark:text-slate-400 max-w-sm">
                                O termo de distrato foi enviado para assinatura via Autentique. O cliente receberá o link por WhatsApp e e-mail.
                            </p>
                            {successData.numero && (
                                <p className="text-xs text-gray-400 dark:text-slate-500">
                                    N° do termo: <span className="font-mono font-semibold">{successData.numero}</span>
                                </p>
                            )}

                            <div className="w-full bg-gray-50 dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700 mt-4 flex items-center justify-between">
                                <span className="text-sm text-gray-600 dark:text-slate-300 truncate mr-4">{successData.link}</span>
                                <button
                                    onClick={handleCopyLink}
                                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm"
                                >
                                    {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                                    {copied ? 'Copiado!' : 'Copiar'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form id="cancel-form" onSubmit={handleGenerateCancellation} onChange={() => setRascunho(true)} className="space-y-6">

                            {/* Seletor de Modelo */}
                            <div className="space-y-3">
                                <h3 className={sectionTitleCls}>
                                    <FileText size={16} className="text-primary" /> Modelo do Distrato
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setModeloDistrato('padrao')}
                                        className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                                            modeloDistrato === 'padrao'
                                                ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                            modeloDistrato === 'padrao' ? 'border-primary' : 'border-gray-300 dark:border-slate-600'
                                        }`}>
                                            {modeloDistrato === 'padrao' && <div className="w-2 h-2 rounded-full bg-primary" />}
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Distrato Padrão</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Rescisão contratual de comum acordo.</p>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setModeloDistrato('inadimplencia')}
                                        className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                                            modeloDistrato === 'inadimplencia'
                                                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                            modeloDistrato === 'inadimplencia' ? 'border-orange-500' : 'border-gray-300 dark:border-slate-600'
                                        }`}>
                                            {modeloDistrato === 'inadimplencia' && <div className="w-2 h-2 rounded-full bg-orange-500" />}
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Distrato — Inadimplência</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Cancelamento administrativo unilateral por falta de pagamento.</p>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            {/* Modo de preenchimento */}
                            <div className="space-y-3">
                                <h3 className={sectionTitleCls}>
                                    <Search size={16} className="text-primary" /> Origem dos Dados
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setModo('buscar')}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                                            modo === 'buscar'
                                                ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <Search size={16} className={`mt-0.5 flex-shrink-0 ${modo === 'buscar' ? 'text-primary' : 'text-gray-400'}`} />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Buscar UC Existente</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Preencher a partir de UC(s) cadastrada(s) de um cliente.</p>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setModo('manual')}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                                            modo === 'manual'
                                                ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <PencilLine size={16} className={`mt-0.5 flex-shrink-0 ${modo === 'manual' ? 'text-primary' : 'text-gray-400'}`} />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Preencher Manualmente</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Digitar todas as informações do termo.</p>
                                        </div>
                                    </button>
                                </div>
                                {modo === 'buscar' && (
                                    <UCSearchPicker
                                        selecionadas={ucsSelecionadas}
                                        onAdd={handleAddUcSelecionada}
                                        onRemove={handleRemoveUcSelecionada}
                                        max={MAX_UCS}
                                        placeholder="Buscar UC por cliente, nº da UC ou CPF/CNPJ..."
                                    />
                                )}
                            </div>

                            {/* Cliente Section */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <User size={16} className="text-primary" /> Dados do Cliente
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className={labelCls}>Nome Completo / Razão Social *</label>
                                        <input type="text" required value={nome} onChange={e => setNome(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Tipo *</label>
                                        <select value={tipoCliente} onChange={e => {
                                            setTipoCliente(e.target.value as 'PF' | 'PJ');
                                            setCpfCnpj('');
                                        }} className={inputCls}>
                                            <option value="PF">Pessoa Física</option>
                                            <option value="PJ">Pessoa Jurídica</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelCls}>CPF / CNPJ *</label>
                                        <input
                                            type="text"
                                            required
                                            value={cpfCnpj}
                                            onChange={handleCpfCnpjChange}
                                            className={inputCls}
                                            placeholder={tipoCliente === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Contato Section */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <Mail size={16} className="text-primary" /> Contato (Autentique)
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelCls}>E-mail *</label>
                                        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>WhatsApp *</label>
                                        <input type="text" required value={whatsapp} onChange={e => setWhatsapp(e.target.value)} className={inputCls} />
                                    </div>
                                </div>
                            </div>

                            {/* Endereço Section */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <MapPin size={16} className="text-primary" /> Endereço do Cliente
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                                    <div className="sm:col-span-2">
                                        <label className={labelCls}>CEP</label>
                                        <input type="text" value={cep} onChange={e => setCep(e.target.value)} className={inputCls} />
                                    </div>
                                    <div className="sm:col-span-4">
                                        <label className={labelCls}>Rua / Logradouro</label>
                                        <input type="text" value={rua} onChange={e => setRua(e.target.value)} className={inputCls} />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className={labelCls}>Número</label>
                                        <input type="text" value={numero} onChange={e => setNumero(e.target.value)} className={inputCls} />
                                    </div>
                                    <div className="sm:col-span-4">
                                        <label className={labelCls}>Bairro</label>
                                        <input type="text" value={bairro} onChange={e => setBairro(e.target.value)} className={inputCls} />
                                    </div>
                                    <div className="sm:col-span-4">
                                        <label className={labelCls}>Cidade</label>
                                        <input type="text" value={cidade} onChange={e => setCidade(e.target.value)} className={inputCls} />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className={labelCls}>Estado (UF)</label>
                                        <input type="text" value={estado} onChange={e => setEstado(e.target.value)} className={inputCls} />
                                    </div>
                                </div>
                            </div>

                            {/* UCs Envolvidas */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <Zap size={16} className="text-primary" /> Unidades Consumidoras Envolvidas (até {MAX_UCS})
                                </h3>
                                <div className="space-y-3">
                                    {ucs.map((entry, idx) => (
                                        <div key={idx} className="bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 rounded-xl p-3 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <p className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">UC {idx + 1}</p>
                                                {ucs.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setUcs(prev => prev.filter((_, i) => i !== idx))}
                                                        className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                    <label className={labelCls}>Número da UC *</label>
                                                    <input type="text" value={entry.numero} onChange={e => updateUcEntry(idx, 'numero', e.target.value)} className={inputCls} />
                                                </div>
                                                <div>
                                                    <label className={labelCls}>Situação</label>
                                                    <input type="text" value={entry.situacao} onChange={e => updateUcEntry(idx, 'situacao', e.target.value)} placeholder="Ex: Ativa" className={inputCls} />
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <label className={labelCls}>Endereço Completo da UC</label>
                                                    <input type="text" value={entry.endereco} onChange={e => updateUcEntry(idx, 'endereco', e.target.value)} placeholder="Rua, número, bairro, cidade/UF" className={inputCls} />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {ucs.length < MAX_UCS && (
                                        <button
                                            type="button"
                                            onClick={() => setUcs(prev => [...prev, emptyUcEntry()])}
                                            className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:opacity-80 transition-opacity"
                                        >
                                            <Plus size={15} /> Adicionar outra UC
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelCls}>Concessionária</label>
                                        <input type="text" value={concessionaria} onChange={e => setConcessionaria(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Data da Solicitação do Cancelamento *</label>
                                        <input
                                            type="date"
                                            required
                                            value={dataSolicitacao}
                                            onChange={e => setDataSolicitacao(e.target.value)}
                                            className={inputCls}
                                        />
                                    </div>
                                    <div className="md:col-start-2">
                                        <label className={labelCls}>Data Efetiva do Desligamento *</label>
                                        <input
                                            type="date"
                                            required
                                            min={dataSolicitacao || undefined}
                                            value={dataEfetiva}
                                            onChange={e => { setDataEfetiva(e.target.value); setDataEfetivaManual(true); }}
                                            className={inputCls}
                                        />
                                        <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                                            {dataEfetivaManual ? (
                                                <>Alterada à mão.{' '}
                                                    <button type="button" onClick={() => setDataEfetivaManual(false)} className="font-semibold text-primary hover:underline">
                                                        Voltar para {prazoAvisoDias} dias
                                                    </button>
                                                </>
                                            ) : (
                                                <>Solicitação + {prazoAvisoDias} dias de aviso prévio do contrato.</>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Signatários internos (rep. legal assina; testemunhas testemunham) */}
                            <SignatariosSelector
                                comProcurador
                                comTestemunhas
                                value={signatarios}
                                onChange={setSignatarios}
                                labelProcurador="Representante Legal da Associação"
                            />

                            {/* Condições financeiras — Padrão */}
                            {modeloDistrato === 'padrao' && (
                                <div className="space-y-4">
                                    <h3 className={sectionTitleCls}>
                                        <FileText size={16} className="text-primary" /> Condições Financeiras (Seção 6)
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelCls}>6.1 — Saldo de Créditos (kWh)</label>
                                            <input type="text" value={saldoKwh} onChange={e => setSaldoKwh(e.target.value)} placeholder="Ex: 1.250" className={inputCls} />
                                        </div>
                                        <div>
                                            <label className={labelCls}>6.1 — Valor Estimado do Saldo (R$)</label>
                                            <input type="text" value={valorSaldo} onChange={e => setValorSaldo(e.target.value)} placeholder="Ex: 1.180,50" className={inputCls} />
                                        </div>
                                    </div>
                                    {sugestaoSaldo ? (() => {
                                        const kwhTxt = sugestaoSaldo.kwh !== null ? fmtNumeroDistrato(sugestaoSaldo.kwh) : null;
                                        const valorTxt = sugestaoSaldo.valor !== null ? fmtNumeroDistrato(sugestaoSaldo.valor) : null;
                                        const meses = Array.from(new Set(sugestaoSaldo.porUc.map(u => u.mesReferencia).filter(Boolean)));
                                        const precos = Array.from(new Set(sugestaoSaldo.porUc.map(u => u.precoKwh).filter((v): v is number => v !== null)));
                                        const diferente = (kwhTxt && saldoKwh.trim() !== kwhTxt) || (valorTxt && valorSaldo.trim() !== valorTxt);
                                        return (
                                            <div className="-mt-1 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 px-3 py-2 text-[11px] text-gray-600 dark:text-slate-300 flex flex-wrap items-center justify-between gap-2">
                                                <span>
                                                    {kwhTxt ? (
                                                        <>
                                                            Pela conta{meses.length ? ` de ${meses.join(', ')}` : ''}: <strong>{kwhTxt} kWh</strong>
                                                            {valorTxt ? (
                                                                <> × R$ {precos.length === 1 ? precos[0].toLocaleString('pt-BR', { maximumFractionDigits: 4 }) : '(preço de cada UC)'}/kWh = <strong>R$ {valorTxt}</strong>
                                                                    <span className="text-gray-400 dark:text-slate-500"> — preço da energia com o desconto do cliente</span></>
                                                            ) : <span className="text-gray-400 dark:text-slate-500"> — a conta não traz a tarifa para estimar o valor</span>}
                                                            {sugestaoSaldo.porUc.some(u => u.precoFonte === 'tarifa_sem_desconto') ? (
                                                                <span className="text-amber-600 dark:text-amber-400"> · tarifa sem desconto informado, confira o valor</span>
                                                            ) : null}
                                                        </>
                                                    ) : 'A conta mais recente não traz o saldo de créditos. Confira na fatura da Equatorial.'}
                                                    {sugestaoSaldo.semSaldo.length && kwhTxt ? <span className="text-amber-600 dark:text-amber-400"> · {sugestaoSaldo.semSaldo.length} UC sem saldo na conta</span> : null}
                                                </span>
                                                {kwhTxt && diferente ? (
                                                    <button type="button" onClick={() => { setRascunho(true); setSaldoKwh(kwhTxt); if (valorTxt) setValorSaldo(valorTxt); }}
                                                        className="shrink-0 px-2.5 py-1 rounded-md border border-primary/30 text-primary font-bold hover:bg-primary/10">
                                                        Usar estes valores
                                                    </button>
                                                ) : null}
                                            </div>
                                        );
                                    })() : null}

                                    <div className="space-y-3 pt-1">
                                        <p className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">6.2 — Registro de débitos em aberto</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelCls}>Faturas Mensais em Atraso (R$)</label>
                                                <input type="text" value={faturasEmAtraso} onChange={e => setFaturasEmAtraso(e.target.value)} placeholder="Ex: 1.560,00" className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Multa por Quebra de Fidelidade (R$)</label>
                                                <input type="text" value={multaFidelidade} onChange={e => setMultaFidelidade(e.target.value)} placeholder="Ex: 850,00" className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Outros Débitos (R$)</label>
                                                <input type="text" value={outrosDebitos} onChange={e => setOutrosDebitos(e.target.value)} placeholder="Ex: 150,00" className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Valor Total em Aberto (R$)</label>
                                                <input type="text" value={totalDebito} onChange={e => setTotalDebito(e.target.value)} placeholder="Ex: 2.560,00" className={inputCls} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <p className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">6.3 — Multa rescisória (múltipla escolha)</p>
                                        {[
                                            { key: 'A', label: 'Opção A — Com fidelidade não cumprida' },
                                            { key: 'B', label: 'Opção B — Sem fidelidade / contrato sem multa' },
                                            { key: 'C', label: 'Opção C — Inadimplência do associado' },
                                        ].map(({ key, label }) => (
                                            <label key={key} className="flex items-center gap-3 cursor-pointer group">
                                                <div
                                                    onClick={() => toggleOpcaoMulta(key)}
                                                    className={`w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors cursor-pointer ${
                                                        opcoesMulta.includes(key)
                                                            ? 'bg-primary border-primary'
                                                            : 'border-gray-300 dark:border-slate-600 group-hover:border-primary'
                                                    }`}
                                                >
                                                    {opcoesMulta.includes(key) && <Check size={12} className="text-white" />}
                                                </div>
                                                <span className="text-sm text-gray-700 dark:text-slate-300">{label}</span>
                                            </label>
                                        ))}
                                        {opcoesMulta.includes('A') && (
                                            <div>
                                                <label className={labelCls}>Valor da Multa Rescisória — Opção A (R$) *</label>
                                                <input type="text" value={multaRescisoria} onChange={e => setMultaRescisoria(e.target.value)} placeholder="Ex: 850,00" className={`${inputCls} sm:w-64`} />
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-3 pt-1">
                                        <p className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">6.4 — Forma de pagamento do débito (item 6.2)</p>
                                        <div className="flex flex-wrap gap-3">
                                            {[
                                                { key: 'a_vista', label: 'À vista' },
                                                { key: 'parcelado', label: 'Parcelado' },
                                                // #6158: sem fatura vencida, o saldo de créditos é abatido nas
                                                // faturas mensais até o desligamento — sem parcelas nem data.
                                                { key: 'compensacao', label: 'Compensação nas próximas faturas' },
                                            ].map(({ key, label }) => (
                                                <label key={key} className="flex items-center gap-2 cursor-pointer">
                                                    <div
                                                        onClick={() => setFormaPagamento(formaPagamento === key ? '' : key as any)}
                                                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors cursor-pointer ${
                                                            formaPagamento === key ? 'border-primary' : 'border-gray-300 dark:border-slate-600'
                                                        }`}
                                                    >
                                                        {formaPagamento === key && <div className="w-2 h-2 rounded-full bg-primary" />}
                                                    </div>
                                                    <span className="text-sm text-gray-700 dark:text-slate-300">{label}</span>
                                                </label>
                                            ))}
                                        </div>
                                        {formaPagamento === 'compensacao' && (
                                            <div className="text-xs text-gray-600 dark:text-slate-400 bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 space-y-1">
                                                <p>O saldo será abatido nas próximas faturas mensais do cliente, até a data efetiva do desligamento. Não é preciso informar parcelas nem data.</p>
                                                {parseFloat(String(faturasEmAtraso || '0').replace(/\./g, '').replace(',', '.')) > 0 && (
                                                    <p className="font-semibold text-amber-700 dark:text-amber-400">
                                                        Atenção: há faturas em atraso no item 6.2. Compensação costuma valer para quem não tem débito vencido.
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        {formaPagamento === 'a_vista' && (
                                            <div>
                                                <label className={labelCls}>Data do Pagamento à Vista *</label>
                                                <input
                                                    type="date"
                                                    value={dataPagamentoAVista}
                                                    onChange={e => setDataPagamentoAVista(e.target.value)}
                                                    className={`${inputCls} sm:w-48`}
                                                />
                                            </div>
                                        )}
                                        {formaPagamento === 'parcelado' && (
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div>
                                                    <label className={labelCls}>Nº de Parcelas *</label>
                                                    <input type="text" value={numParcelas} onChange={e => setNumParcelas(e.target.value)} placeholder="Ex: 6" className={inputCls} />
                                                </div>
                                                <div>
                                                    <label className={labelCls}>Valor da Parcela (R$) *</label>
                                                    <input type="text" value={valorParcela} onChange={e => setValorParcela(e.target.value)} placeholder="Ex: 390,00" className={inputCls} />
                                                </div>
                                                <div>
                                                    <label className={labelCls}>Data da 1ª Parcela *</label>
                                                    <input type="date" value={dataPrimeiraParcela} onChange={e => setDataPrimeiraParcela(e.target.value)} className={inputCls} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Inadimplência */}
                            {modeloDistrato === 'inadimplencia' && (
                                <>
                                    <div className="space-y-3">
                                        <h3 className={sectionTitleCls}>
                                            <AlertTriangle size={16} className="text-orange-500" /> Motivos da Inadimplência (múltipla escolha) *
                                        </h3>
                                        {[
                                            { key: 'falta_pagamento', label: 'Falta de pagamento de faturas mensais consecutivas' },
                                            { key: 'insucesso_contato', label: 'Insucesso nas tentativas de contato e negociação amigável' },
                                            { key: 'abandono', label: 'Abandono do contrato sem comunicação prévia' },
                                        ].map(({ key, label }) => (
                                            <label key={key} className="flex items-center gap-3 cursor-pointer group">
                                                <div
                                                    onClick={() => toggleMotivoInadi(key)}
                                                    className={`w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors cursor-pointer ${
                                                        motivosInadi.includes(key)
                                                            ? 'bg-orange-500 border-orange-500'
                                                            : 'border-gray-300 dark:border-slate-600 group-hover:border-orange-400'
                                                    }`}
                                                >
                                                    {motivosInadi.includes(key) && <Check size={12} className="text-white" />}
                                                </div>
                                                <span className="text-sm text-gray-700 dark:text-slate-300">{label}</span>
                                            </label>
                                        ))}
                                    </div>

                                    <div className="space-y-4">
                                        <h3 className={sectionTitleCls}>
                                            <FileText size={16} className="text-orange-500" /> Registro de Débitos em Aberto
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelCls}>Faturas em Atraso (R$)</label>
                                                <input type="text" value={faturasEmAtraso} onChange={e => setFaturasEmAtraso(e.target.value)} placeholder="Ex: 1.560,00" className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Multa por Quebra de Fidelidade (R$)</label>
                                                <input type="text" value={multaFidelidade} onChange={e => setMultaFidelidade(e.target.value)} placeholder="Ex: 850,00" className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Outros Débitos (R$)</label>
                                                <input type="text" value={outrosDebitos} onChange={e => setOutrosDebitos(e.target.value)} placeholder="Ex: 150,00" className={inputCls} />
                                            </div>
                                            <div>
                                                <label className={labelCls}>Valor Total em Aberto (R$)</label>
                                                <input type="text" value={totalDebito} onChange={e => setTotalDebito(e.target.value)} placeholder="Ex: 2.560,00" className={inputCls} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <h3 className={sectionTitleCls}>
                                            <PhoneCall size={16} className="text-orange-500" /> Histórico de Tentativas de Contato (até {MAX_CONTATOS})
                                        </h3>
                                        {contatos.map((c, idx) => (
                                            <div key={idx} className="bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 rounded-xl p-3 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Tentativa {idx + 1}</p>
                                                    {contatos.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setContatos(prev => prev.filter((_, i) => i !== idx))}
                                                            className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                    <div>
                                                        <label className={labelCls}>Data</label>
                                                        <input type="date" value={c.data} onChange={e => updateContato(idx, 'data', e.target.value)} className={inputCls} />
                                                    </div>
                                                    <div>
                                                        <label className={labelCls}>Meio</label>
                                                        <select value={c.meio} onChange={e => updateContato(idx, 'meio', e.target.value)} className={inputCls}>
                                                            <option value="">Selecione...</option>
                                                            <option value="WhatsApp">WhatsApp</option>
                                                            <option value="E-mail">E-mail</option>
                                                            <option value="Telefone">Telefone</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className={labelCls}>Resultado</label>
                                                        <input type="text" value={c.resultado} onChange={e => updateContato(idx, 'resultado', e.target.value)} placeholder="Ex: Sem resposta" className={inputCls} />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {contatos.length < MAX_CONTATOS && (
                                            <button
                                                type="button"
                                                onClick={() => setContatos(prev => [...prev, emptyContato()])}
                                                className="flex items-center gap-1.5 text-sm font-semibold text-orange-500 hover:text-orange-600 transition-colors"
                                            >
                                                <Plus size={15} /> Adicionar tentativa de contato
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Motivo do Desligamento — apenas Padrão */}
                            {modeloDistrato === 'padrao' && (
                                <div>
                                    <label className={labelCls}>Motivo do Desligamento (Opcional)</label>
                                    <textarea
                                        value={motivoDesligamento}
                                        onChange={e => setMotivoDesligamento(e.target.value)}
                                        rows={2}
                                        placeholder="Ex: Mudança de endereço, venda do imóvel, insatisfação com o serviço..."
                                        className={`${inputCls} resize-y`}
                                    />
                                </div>
                            )}

                            {/* Observações Adicionais */}
                            <div>
                                <label className={labelCls}>Observações Adicionais (Opcional)</label>
                                <textarea
                                    value={obs}
                                    onChange={e => setObs(e.target.value)}
                                    rows={3}
                                    placeholder="Adicione observações complementares para constar no termo de cancelamento..."
                                    className={`${inputCls} resize-y`}
                                />
                            </div>

                        </form>
                    )}
                </div>

                {/* Footer */}
                {!successData && (
                    <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={pedirParaFechar}
                            disabled={loading}
                            className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            form="cancel-form"
                            disabled={loading}
                            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold transition-colors shadow-sm disabled:opacity-70 flex items-center justify-center min-w-[140px]"
                        >
                            {loading ? <Loader2 size={16} className="animate-spin" /> : 'Gerar Distrato'}
                        </button>
                    </div>
                )}
            </div>
        </div>

        <ConfirmModal
            isOpen={!!conferencia}
            onClose={() => setConferencia(null)}
            onConfirm={() => {
                const emailJaDevolvido = !!conferencia?.emailJaDevolvido;
                setConferencia(null);
                emitirDistrato(emailJaDevolvido);
            }}
            title="Confira antes de gerar"
            message="Antes de gastar um documento no Autentique:"
            confirmLabel="Gerar mesmo assim"
            cancelLabel="Voltar e corrigir"
            variant="warning"
        >
            <ul className="mt-3 space-y-2 text-sm text-gray-600 dark:text-slate-300 list-disc pl-5">
                {conferencia?.avisos.map(aviso => <li key={aviso}>{aviso}</li>)}
            </ul>
        </ConfirmModal>

        <ConfirmModal
            isOpen={confirmandoDescarte}
            onClose={continuarPreenchendo}
            onConfirm={descartarEFechar}
            title="Descartar o distrato?"
            message="Você já preencheu dados deste distrato. Se fechar agora, tudo o que foi digitado se perde."
            confirmLabel="Descartar"
            cancelLabel="Continuar preenchendo"
            variant="warning"
        />
        </>,
        document.body
    );
};

export default CancelUCModal;
